import styles from './downloadFilesModal.module.css'
import { useEffect, useState } from 'react'
import { BooleanField, Modal, ModalId, showErrorStrToast, useUIState } from '../../../ui'
import { usePDFActions } from '../../actions/pdfActions'
import { useFileActions } from '../../actions/fileActions'
import { printPdfBlob, mergePdfBlobs } from '../../utils/printUtils'
import { FileOption } from './types'
import { delay } from '../../../../utils/codeUtils'
import { dedupe } from '../../../../utils/listUtils'
import { useServer } from '../../../../services/server'

type Props = {
    fileOptions: FileOption[]
    modalId?: ModalId
    // Groups only help to tell kinds of documents apart: a modal with a single kind lists them all
    grouped?: boolean
}

export const DownloadFilesModal = (props: Props) => {
    const { fileOptions, modalId = ModalId.DOWNLOAD_FILES, grouped = true } = props

    const [selectedIds, setSelectedIds] = useState<string[]>([])
    const [expandedGroups, setExpandedGroups] = useState<string[]>([])
    const [isPrinting, setIsPrinting] = useState(false)

    const { openedModal } = useUIState()

    const [key, setKey] = useState(0)

    const { downloadFile, fetchFile } = useFileActions()
    const { downloadFileFromUrl } = useServer()
    const { generatePDF, generatePDFBlob } = usePDFActions()

    useEffect(() => {
        setKey(key + 1)
        setExpandedGroups([])
    }, [openedModal])

    const getGroupOptions = (group: string) => fileOptions.filter(fileOption => fileOption.group === group)

    const isGrouped = (fileOption: FileOption) => grouped && !!fileOption.group && getGroupOptions(fileOption.group).length > 1

    const onSelectAllFiles = (value?: boolean) => {
        setSelectedIds(value ? fileOptions.map(fileOption => fileOption.id) : [])
    }

    const onSelectFile = (id: string) => (value?: boolean) => {
        const newSelectedIds = value ? [...selectedIds, id] : selectedIds.filter(selectedId => selectedId !== id)
        setSelectedIds(newSelectedIds)
    }

    const onSelectGroup = (groupIds: string[]) => (value?: boolean) => {
        setSelectedIds(value ?
            dedupe([...selectedIds, ...groupIds]) :
            selectedIds.filter(selectedId => !groupIds.includes(selectedId))
        )
    }

    const onToggleGroup = (group: string) => () => {
        setExpandedGroups(expandedGroups.includes(group) ?
            expandedGroups.filter(expandedGroup => expandedGroup !== group) :
            [...expandedGroups, group]
        )
    }

    const onSubmit = async () => {
        for (const fileOptionId of selectedIds) {
            const fileOption = fileOptions.find(fileOption => fileOption.id === fileOptionId)!
            if (fileOption?.url) {
                await downloadFile(fileOption.url)
            } else if (fileOption?.webUrl) {
                await downloadFileFromUrl(fileOption.webUrl, fileOption.fileName)
            } else if (fileOption?.pdf) {
                generatePDF(fileOption.pdf.id, fileOption.fileName)
            } else if (fileOption?.csv) {
                fileOption.csv.download()
            }
            await delay(0.5)
        }

        setSelectedIds([])
    }

    const onCancel = () => setSelectedIds([])

    const printableIds = selectedIds.filter(id => {
        const fo = fileOptions.find(f => f.id === id)
        return fo?.pdf || fo?.url
    })

    const handlePrint = async () => {
        if (printableIds.length === 0) return
        try {
            setIsPrinting(true)
            const blobs: Blob[] = []

            for (const fileOptionId of printableIds) {
                const fileOption = fileOptions.find(fo => fo.id === fileOptionId)!
                if (fileOption.pdf) {
                    blobs.push(await generatePDFBlob(fileOption.pdf.id))
                } else if (fileOption.url) {
                    blobs.push(await fetchFile(fileOption.url))
                }
            }

            const blobToPrint = blobs.length === 1 ? blobs[0] : await mergePdfBlobs(blobs)
            await printPdfBlob(blobToPrint)
        } catch {
            showErrorStrToast('Error al generar los documentos para imprimir.')
        } finally {
            setIsPrinting(false)
        }
    }

    const renderOption = (fileOption: FileOption) => (
        <BooleanField
            key={fileOption.id}
            id={`check-${fileOption.id}`}
            value={selectedIds.includes(fileOption.id)}
            label={fileOption.label}
            onChange={onSelectFile(fileOption.id)}
        />
    )

    const renderGroup = (group: string) => {
        const groupOptions = getGroupOptions(group)
        const groupIds = groupOptions.map(fileOption => fileOption.id)
        const expanded = expandedGroups.includes(group)
        return (
            <div key={`group-${group}`}>
                <div className={styles.groupHeader}>
                    <BooleanField
                        id={`check-group-${group.replace(/\W+/g, '-')}`}
                        value={groupIds.every(groupId => selectedIds.includes(groupId))}
                        label={`${group} (${groupOptions.length})`}
                        onChange={onSelectGroup(groupIds)}
                    />
                    <button
                        type="button"
                        className={styles.expandButton}
                        title={expanded ? 'Ocultar' : 'Ver todos'}
                        aria-expanded={expanded}
                        onClick={onToggleGroup(group)}
                    >
                        <i className={`bi bi-chevron-down ${styles.expandIcon} ${expanded ? styles.expanded : ''}`} />
                    </button>
                </div>
                {expanded && (
                    <div className={styles.groupOptions}>
                        {groupOptions.map(renderOption)}
                    </div>
                )}
            </div>
        )
    }

    // A group is shown where its first option is
    const renderOptions = () => fileOptions.map((fileOption, index) => {
        if (!isGrouped(fileOption)) return renderOption(fileOption)
        const isFirstOfGroup = fileOptions.findIndex(other => other.group === fileOption.group) === index
        return isFirstOfGroup ? renderGroup(fileOption.group!) : null
    })

    return (
        <Modal
            modalId={modalId}
            title="Descargar Documentos"
            submitLabel={selectedIds.length > 0 ? `Descargar (${selectedIds.length})` : "Descargar"}
            submitIcon="download"
            disableSubmit={selectedIds.length === 0}
            onSubmit={onSubmit}
            onCancel={onCancel}
            secondaryLabel={printableIds.length > 0 ? `Imprimir (${printableIds.length})` : "Imprimir"}
            secondaryIcon="printer"
            disableSecondary={isPrinting || printableIds.length === 0}
            onSecondarySubmit={handlePrint}
        >
            <div>
                <p>Seleccionar los documentos a descargar: </p>
                {fileOptions.length >= 2 && (
                    <div className={styles.selectAll}>
                        <BooleanField
                            id="all"
                            value={selectedIds.length === fileOptions.length}
                            label="Seleccionar todos"
                            onChange={onSelectAllFiles}
                        />
                    </div>
                )}
                {renderOptions()}
                <div className={styles.hidden}>
                    {fileOptions
                        .filter(fileOption => fileOption.pdf && selectedIds.includes(fileOption.id))
                        .map(fileOption => (
                            <div key={fileOption.id}>
                                {fileOption.pdf!.component}
                            </div>
                        ))
                    }
                </div>
            </div>
        </Modal>
    )
}
